import { useState, useEffect } from 'react';
import { useField } from 'formik';
import debounce from 'debounce-promise';
import { uniqBy as uniqby } from 'lodash-es';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';

export type OptionType = Record<string, any>;

export interface FetchConfig {
  endPoint?: string;
  [key: string]: any;
}

export interface UseAsyncSelectOptions {
  name: string;
  staticOptions: OptionType[];
  isMulti: boolean;
  valueKey: string;
  labelKey: string;
  pluckById?: boolean;
  fetchConfig: FetchConfig;
  useInput: boolean;
  initialSearchQuery: string;
  loadDefaultOptions: boolean;
  defaultOptions: OptionType[];
  refreshOptions?: boolean;
  customOnChange?: (selectedOption: any) => void;
  initialSelectedOption?: OptionType | null;
}

export const useAsyncSelect = ({
  name,
  staticOptions,
  isMulti,
  valueKey,
  pluckById,
  labelKey,
  fetchConfig,
  useInput,
  initialSearchQuery,
  loadDefaultOptions,
  defaultOptions,
  refreshOptions,
  customOnChange = () => {},
  initialSelectedOption,
}: UseAsyncSelectOptions) => {
  const [_field, meta, helpers] = useField(name); // eslint-disable-line
  const { setValue, setError } = helpers;
  const { value: formikValue } = meta;

  // Initialize with initialSelectedOption if provided
  const [selectedOptions, setSelectedOptions] = useState<OptionType[]>(() => {
    if (initialSelectedOption) {
      return [initialSelectedOption];
    }
    return [];
  });
  const [initialFetchedOptions, setInitialFetchedOptions] = useState<
    OptionType[]
  >([]);

  // Set initial formik value on mount if initialSelectedOption is provided
  useEffect(() => {
    if (initialSelectedOption) {
      setValue(initialSelectedOption[valueKey]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset field value when refreshOptions changes
  useEffect(() => {
    setValue(isMulti ? [] : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOptions]);

  // Load initial options on component mount
  useEffect(() => {
    if (!fetchConfig?.endPoint) return;
    if (useInput && !initialSearchQuery) return;

    const loadInitialOptions = async () => {
      const endPoint = useInput
        ? `${fetchConfig.endPoint}${initialSearchQuery}`
        : fetchConfig.endPoint;

      try {
        const response = await fetchSelectOptions({
          ...fetchConfig,
          endPoint,
        });

        setInitialFetchedOptions((prevOptions) => {
          const allExistingOptions = [
            ...staticOptions,
            ...prevOptions,
            ...defaultOptions,
          ];

          const uniqueNewOptions = response.filter(
            (newOption: OptionType) =>
              !allExistingOptions.some(
                (existingOption) =>
                  existingOption[valueKey] === newOption[valueKey]
              )
          );

          return [...prevOptions, ...uniqueNewOptions];
        });
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };

    // Only perform the initial fetch if loadDefaultOptions is true
    if (loadDefaultOptions) {
      loadInitialOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchQuery]);

  // Option calculation helpers
  const allOptions = () => [
    ...staticOptions,
    ...selectedOptions,
    ...defaultOptions,
  ];

  const uniqueOptions = () => {
    if (!meta?.value) return [];
    const options = allOptions();
    return uniqby(
      options.filter((item) => meta.value?.includes?.(item?.[valueKey])),
      valueKey
    );
  };

  const formikValueOptions = () => {
    if (!meta?.value) return [];
    const options = allOptions();
    const option = options.find((item) =>
      pluckById ? item?.id === meta.value.id : item?.[valueKey] === meta.value
    );
    if (!option) return [];
    return uniqby(Array.isArray(option) ? option : [option], valueKey);
  };

  // Calculate current values based on formik state
  const getValues = (validationType?: string) => {
    if (!formikValue) return null;
    if (isMulti) {
      return uniqueOptions();
    } else if (validationType === 'object') {
      if (!formikValue[labelKey]) return null;
      return formikValue;
    } else {
      return formikValueOptions();
    }
  };

  const uniqueDefaultOptionsWithInput = uniqby(
    [...staticOptions, ...initialFetchedOptions, ...defaultOptions],
    valueKey
  );

  // Event handlers
  const onChange = (selectedOption: any) => {
    customOnChange(selectedOption);

    setSelectedOptions(
      selectedOption ? (isMulti ? [...selectedOption] : [selectedOption]) : []
    );

    setValue(
      isMulti && selectedOption
        ? selectedOption.map((item: OptionType) => item?.[valueKey])
        : selectedOption?.[valueKey]
    );

    setError(null);
  };

  // Data loading
  const debouncedLoadOptions = debounce(async (input: string) => {
    if (!fetchConfig?.endPoint) return [];
    if (useInput && !input && !initialSearchQuery) return [];

    const endPoint = useInput
      ? `${fetchConfig.endPoint}${input || initialSearchQuery}`
      : fetchConfig.endPoint;

    try {
      return await fetchSelectOptions({ ...fetchConfig, endPoint });
    } catch (error) {
      console.error('Error loading options:', error);
      return [];
    }
  }, 300);

  return {
    meta,
    getValues,
    selectedOptions,
    uniqueDefaultOptionsWithInput,
    onChange,
    debouncedLoadOptions,
  };
};
